/**
 * useOfflineQueue Hook
 * 
 * Manages a queue of progress updates and other data that should be synced
 * when the device comes back online. Automatically processes queued items
 * when connection is restored.
 * 
 * @module useOfflineQueue
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { logger } from '../utils/logger';
import { progressSync } from '../utils/progressSync';
import { useUser } from '../contexts/UserContext';

/**
 * Hook for managing offline queue of updates that need to be synced when online
 * 
 * @returns {Object} Queue state and management functions
 * @returns {Array} returns.queue - Array of queued updates
 * @returns {boolean} returns.isOnline - Current online status
 * @returns {boolean} returns.isProcessing - Whether queue is being processed
 * @returns {number} returns.queueSize - Number of items in queue
 * @returns {Function} returns.queueUpdate - Function to queue an update
 * @returns {Function} returns.processQueue - Function to manually process queue
 * @returns {Function} returns.clearQueue - Function to clear the queue
 * @returns {Function} returns.removeItem - Function to remove specific item from queue
 * 
 * @example
 * const { queueUpdate, isOnline, queueSize } = useOfflineQueue();
 * 
 * // Queue a progress update
 * queueUpdate({
 *   type: 'progress',
 *   data: { totalScore: 100 },
 *   timestamp: Date.now()
 * });
 */
export const useOfflineQueue = () => {
  const { user } = useUser();
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine !== undefined) {
      return navigator.onLine;
    }
    return true; // Default to online if navigator is not available
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [queue, setQueue] = useState(() => {
    // Load existing queue from storage
    try {
      const saved = storage.get(STORAGE_KEYS.OFFLINE_QUEUE) || [];
      return saved;
    } catch (error) {
      logger.warn('Failed to load offline queue from storage:', error);
      return [];
    }
  });
  const processingRef = useRef(false);

  // Save queue to storage whenever it changes
  useEffect(() => {
    storage.set(STORAGE_KEYS.OFFLINE_QUEUE, queue);
  }, [queue]);

  /**
   * Processes all queued updates when online
   * 
   * @returns {Promise<Object>} Result object with success/failure counts
   */
  const processQueue = useCallback(async () => {
    if (!isOnline || processingRef.current || queue.length === 0) {
      return { success: 0, failed: 0, total: queue.length };
    }

    processingRef.current = true;
    setIsProcessing(true);

    const results = {
      success: 0,
      failed: 0,
      total: queue.length,
      errors: []
    };

    logger.info(`Processing ${queue.length} queued updates...`);

    // Process queue items sequentially to avoid overwhelming the server
    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      
      try {
        switch (item.type) {
          case 'progress':
            if (user && !user.is_guest) {
              await progressSync.syncProgress(item.data, user, true);
              results.success++;
              logger.debug(`Synced progress update: ${item.id}`);
            } else {
              // Skip if not authenticated
              results.success++;
            }
            break;
          default:
            logger.warn(`Unknown queue item type: ${item.type}`);
            results.failed++;
        }
      } catch (error) {
        logger.error(`Failed to process queue item ${item.id}:`, error);
        results.failed++;
        results.errors.push({ id: item.id, error: error.message });
      }
    }

    // Remove processed items from queue
    if (results.success > 0) {
      setQueue(prevQueue => prevQueue.slice(results.success));
      storage.set(STORAGE_KEYS.OFFLINE_QUEUE, queue.slice(results.success));
    }

    processingRef.current = false;
    setIsProcessing(false);

    logger.info(`Queue processing complete: ${results.success} succeeded, ${results.failed} failed`);
    return results;
  }, [isOnline, queue, user]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      logger.info('Connection restored, processing offline queue...');
      // Process queue when coming back online
      processQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
      logger.info('Connection lost, updates will be queued');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsOnline(false);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processQueue]);

  /**
   * Adds an item to the offline queue
   * 
   * @param {Object} update - Update object to queue
   * @param {string} update.type - Type of update ('progress', 'achievement', 'custom')
   * @param {Object} update.data - Data to sync
   * @param {Function} [update.syncFunction] - Custom sync function (for 'custom' type)
   * @param {number} [update.priority] - Priority level (higher = more important)
   * @returns {string} ID of the queued item
   * 
   * @example
   * const id = queueUpdate({
   *   type: 'progress',
   *   data: { totalScore: 100 },
   *   priority: 1
   * });
   */
  const queueUpdate = useCallback((update) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    
    const queueItem = {
      id,
      type: update.type || 'custom',
      data: update.data,
      syncFunction: update.syncFunction,
      priority: update.priority || 0,
      timestamp: Date.now(),
      retryCount: 0
    };

    setQueue(prev => {
      // Add new item and sort by priority (higher priority first)
      const newQueue = [...prev, queueItem];
      return newQueue.sort((a, b) => b.priority - a.priority);
    });

    logger.debug(`Queued update: ${id} (type: ${queueItem.type})`);

    // If online, try to process immediately
    if (isOnline && !processingRef.current) {
      setTimeout(() => processQueue(), 100);
    }

    return id;
  }, [isOnline, processQueue]);

  /**
   * Removes a specific item from the queue
   * 
   * @param {string} itemId - ID of the item to remove
   */
  const removeItem = useCallback((itemId) => {
    setQueue(prev => prev.filter(item => item.id !== itemId));
    logger.debug(`Removed queue item: ${itemId}`);
  }, []);

  /**
   * Clears all items from the queue
   */
  const clearQueue = useCallback(() => {
    setQueue([]);
    storage.remove(STORAGE_KEYS.OFFLINE_QUEUE);
    logger.info('Offline queue cleared');
  }, []);

  return {
    queue,
    isOnline,
    isProcessing,
    queueSize: queue.length,
    queueUpdate,
    processQueue,
    clearQueue,
    removeItem
  };
};

